import React, { useState, useEffect, useRef } from 'react';
import { getProperImageUrl } from './utils';
import { ListMusic, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Song, Album } from './types';
import ScrollingText from './ScrollingText';

interface AlbumDisplayProps {
  currentAlbum: Album | null;
  currentSong: Song | null;
  coverArt: string;
  isPlaying: boolean;
  albumButtonRef: React.RefObject<HTMLButtonElement>;
  setShowAlbums: (show: boolean) => void;
  showAlbums: boolean;
  setShowPlaylist: (show: boolean) => void;
  showTooltip: boolean;
  setShowTooltip: (show: boolean) => void;
}

export const AlbumDisplay: React.FC<AlbumDisplayProps> = ({
  currentAlbum,
  currentSong,
  coverArt,
  isPlaying,
  albumButtonRef,
  setShowAlbums,
  showAlbums,
  setShowPlaylist,
  showTooltip,
  setShowTooltip
}) => {
  // Helper function to get proper image URL - shared from AlbumList component
  return (
    <div className="flex items-center space-x-4">
      <div className="relative flex items-center">
        <AnimatePresence>
          {showTooltip && (
            <div className="absolute right-full flex items-center mr-2">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="flex items-center"
              >
                <div className="bg-white text-black px-2 flex items-center h-5">
                  <span className="text-xs uppercase tracking-wider whitespace-nowrap leading-none">Select Album</span>
                </div>
                <ChevronRight className="text-white ml-1" size={20} />
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        <motion.button
          ref={albumButtonRef}
          onClick={() => {
            setShowAlbums(!showAlbums);
            setShowPlaylist(false);
            setShowTooltip(false);
          }}
          animate={{
            scale: isPlaying ? [1, 1.2, 1] : 1
          }}
          transition={{ duration: 0.5 }}
          className="relative w-12 h-12 bg-white overflow-hidden"
        >
          {coverArt ? (
            <img 
              src={getProperImageUrl(coverArt)} 
              alt={currentAlbum?.name || 'Unknown Album'}
              className="w-full h-full object-cover filter grayscale"
            />
          ) : (
            <div className="w-full h-full bg-zinc-800" />
          )}
        </motion.button>
      </div>
      <div>
        <ScrollingText 
          text={currentSong?.name || ''} 
          className="text-xl uppercase tracking-wider"
        />
        <ScrollingText 
          text={currentAlbum?.name || 'Unknown Album'} 
          className="text-gray-400 uppercase tracking-wider"
        />
      </div>
    </div>
  );
};

interface PlaylistButtonProps {
  playlistButtonRef: React.RefObject<HTMLButtonElement>;
  showPlaylist: boolean;
  setShowPlaylist: (show: boolean) => void;
  setShowAlbums: (show: boolean) => void;
}

export const PlaylistButton: React.FC<PlaylistButtonProps> = ({
  playlistButtonRef,
  showPlaylist,
  setShowPlaylist,
  setShowAlbums
}) => {
  return (
    <button
      ref={playlistButtonRef}
      onClick={() => {
        setShowPlaylist(!showPlaylist);
        setShowAlbums(false);
      }}
      className="text-gray-400 hover:text-white transition-colors"
    >
      <ListMusic size={24} />
    </button>
  );
};

interface PlaylistProps {
  currentAlbum: Album | null;
  currentSong: Song | null;
  selectSong: (song: Song) => void;
  playlistMenuRef: React.RefObject<HTMLDivElement>;
}

export const Playlist: React.FC<PlaylistProps> = ({
  currentAlbum,
  currentSong,
  selectSong,
  playlistMenuRef
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showTopArrow, setShowTopArrow] = useState(false);
  const [showBottomArrow, setShowBottomArrow] = useState(false);

  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isScrollable = scrollHeight > clientHeight;

    // Show top arrow if scrolled down
    setShowTopArrow(scrollTop > 10);

    // Show bottom arrow if not at bottom and content is scrollable
    setShowBottomArrow(isScrollable && scrollTop < scrollHeight - clientHeight - 10);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Check initially
    checkScroll();

    // Add scroll listener
    container.addEventListener('scroll', checkScroll);

    // Check on resize
    window.addEventListener('resize', checkScroll);

    return () => {
      container.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, [currentAlbum]);

  if (!currentAlbum) return null;

  return (
    <div className="relative">
      {/* Top scroll indicator */}
      <AnimatePresence>
        {showTopArrow && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute top-0 left-0 right-0 z-10 flex justify-center pointer-events-none py-2"
          >
            <ChevronUp className="text-red-500 animate-bounce" size={24} strokeWidth={3} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Scrollable content */}
      <div
        ref={scrollContainerRef}
        className="max-h-[calc(100%-180px)] overflow-y-auto"
      >
        {currentAlbum.songs.map((song) => {
          const isActive = currentSong && currentSong.id === song.id;
          return (
            <div
              key={song.id}
              className={`flex items-center p-2 hover:bg-gray-700 cursor-pointer ${
                isActive ? 'bg-blue-800' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation();
                selectSong(song);
              }}
            >
              <div className="text-sm">
                {song.name}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom scroll indicator */}
      <AnimatePresence>
        {showBottomArrow && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute bottom-0 left-0 right-0 z-10 flex justify-center pointer-events-none py-2"
          >
            <ChevronDown className="text-red-500 animate-bounce" size={24} strokeWidth={3} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface AlbumListProps {
  albums: Album[];
  currentAlbum: Album | null;
  setCurrentAlbum: (album: Album) => void;
  setShowAlbums: (show: boolean) => void;
  albumMenuRef: React.RefObject<HTMLDivElement>;
}

export const AlbumList: React.FC<AlbumListProps> = ({
  albums,
  currentAlbum,
  setCurrentAlbum,
  setShowAlbums,
  albumMenuRef
}) => {
  const isActive = (album: Album) => currentAlbum?.id === album.id;
  
  // Helper function to get proper image URL
  return (
    <div 
      ref={albumMenuRef}
      className="absolute bottom-full left-0 bg-zinc-800 border border-zinc-700 w-56 max-h-60 overflow-y-auto z-10 scrollbar-thin scrollbar-thumb-zinc-600 scrollbar-track-zinc-800 py-1 rounded shadow-lg"
    >
      {albums.map((album) => {
        return (
          <div
            key={album.id}
            className={`flex items-center p-2 hover:bg-gray-700 cursor-pointer ${
              isActive(album) ? 'bg-blue-800' : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentAlbum(album);
              setShowAlbums(false);
              localStorage.setItem('currentAlbumId', album.id);
            }}
          >
            <div className="flex items-center">
              {album.coverUrl && (
                <img 
                  src={getProperImageUrl(album.coverUrl)} 
                  alt={album.name}
                  className="w-8 h-8 mr-2 object-cover filter grayscale"
                />
              )}
              <div className="text-sm">{album.name}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}; 